<template>
  <div class="relative">
    <aside
      class="fixed top-0 left-0 h-screen flex flex-col bg-white dark:bg-slate-800/95 border-r border-neutral-200 dark:border-slate-700/50 transition-all duration-300"
      :class="{ 'w-[256px]': layoutStore.isExpanded, 'w-[72px]': !layoutStore.isExpanded }"
    >
      <!-- Header -->
      <div class="shrink-0 p-4 flex items-center border-b border-neutral-200 dark:border-slate-700/50 bg-linear-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/90">
        <div class="flex items-center justify-left w-full">
          <img v-if="layoutStore.isExpanded" :alt="t('common.logo_full')" class="w-40" src="@/assets/Logo.svg" />
          <img v-else :alt="t('common.logo_icon')" class="h-7" src="@/assets/CEETRAC-logo-icon.png" />
        </div>
        <!-- Expanded state -->
        <button
          v-if="layoutStore.isExpanded"
          class="p-1 hover:bg-neutral-100 dark:hover:bg-slate-800 rounded-lg absolute right-4 transition-colors duration-200"
          :title="t('common.collapse_sidebar')"
          @click="layoutStore.toggleSidebar"
        >
          <ChevronLeftIcon class="w-5 h-5 text-neutral-500 dark:text-slate-400" />
        </button>
      </div>

      <!-- Search -->
      <div v-if="layoutStore.isExpanded" class="p-4 border-b border-neutral-200 dark:border-slate-700/50 bg-white dark:bg-slate-800">
        <div class="relative">
          <input
            type="text"
            :placeholder="t('common.search')"
            class="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-500 transition-all duration-200"
          />
          <MagnifyingGlassIcon class="w-4 h-4 text-neutral-500 dark:text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        </div>
      </div>
      <div v-else class="p-4 border-b border-neutral-200 dark:border-slate-700/50">
        <button 
          class="w-full h-full flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200"
          @mouseenter="showTooltip($event, t('common.search'))"
          @mouseleave="hideTooltip"
        >
          <MagnifyingGlassIcon class="w-5 h-5 text-neutral-500 dark:text-slate-400" />
        </button>
      </div>

      <!-- Main Navigation -->
      <nav class="flex-1 overflow-y-auto p-2 space-y-1 bg-linear-to-b from-white via-white to-slate-50 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800/95">
        <template v-for="item in mainNavigationItems" :key="item.label">
          <div class="space-y-1">
            <router-link
              v-if="item.path"
              :key="item.label"
              :to="item.path"
              class="flex items-center !px-2 !py-2 text-sm rounded-[4px] transition-all duration-200"
              :class="{ 
                '!text-primary-700 !dark:text-primary-400 bg-primary-50/50 dark:bg-slate-800/50': isActive(item.path),
                '!text-neutral-700 !dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-200': !isActive(item.path)
              }"
            >
                <component :is="item.icon" class="w-5 h-5 shrink-0" />
                <span v-if="layoutStore.isExpanded" class="ml-3 truncate">{{ item.label }}</span>
            </router-link>
            <button
              v-else
              class="w-full flex items-center justify-between px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
              :class="{
                'bg-primary-50/80 dark:bg-slate-800 text-primary-700 dark:text-primary-400': isActiveParent(item),
                'text-neutral-700 dark:text-slate-300 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-100': !isActiveParent(item)
              }"
              @click="handleIconClick(item)"
              @mouseenter="showTooltip($event, item.label)"
              @mouseleave="hideTooltip"
            >
              <div class="flex items-center min-w-0" :class="{ 'justify-center w-full': !layoutStore.isExpanded }">
                <component :is="item.icon" class="w-5 h-5 shrink-0" />
                <span v-if="layoutStore.isExpanded" class="ml-3 truncate">{{ item.label }}</span>
              </div>
              <ChevronDownIcon
                v-if="layoutStore.isExpanded"
                class="w-4 h-4 transition-transform text-neutral-500 dark:text-slate-400 shrink-0"
                :class="{ 'rotate-180': item.open }"
              />
            </button>
            <div v-if="layoutStore.isExpanded && item.open" class="pl-4 space-y-1">
              <router-link
                v-for="child in item.children"
                :key="child.label"
                :to="child.path"
                class="flex items-center px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
                :class="{ 
                  'text-primary-700 dark:text-primary-400 bg-primary-50/50 dark:bg-slate-800/50': isActive(child.path),
                  'text-neutral-700 dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-200': !isActive(child.path)
                }"
              >
                {{ child.label }}
              </router-link>
            </div>
          </div>
        </template>
      </nav>

      <!-- Footer Navigation -->
      <nav class="p-2 space-y-1 border-t border-neutral-200 dark:border-slate-700/50 bg-white dark:bg-slate-800">
        <template v-for="item in footerNavigationItems" :key="item.label">
          <div class="space-y-1">
            <button
              class="w-full flex items-center justify-between px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
              :class="{
                'bg-primary-50/80 dark:bg-slate-800 text-primary-700 dark:text-primary-400': isActiveParent(item),
                'text-neutral-700 dark:text-slate-300 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-100': !isActiveParent(item)
              }"
              @click="handleIconClick(item)"
              @mouseenter="showTooltip($event, item.label)"
              @mouseleave="hideTooltip"
            >
              <div class="flex items-center min-w-0" :class="{ 'justify-center w-full': !layoutStore.isExpanded }">
                <component :is="item.icon" class="w-5 h-5 shrink-0" />
                <span v-if="layoutStore.isExpanded" class="ml-3 truncate">{{ item.label }}</span>
              </div>
              <ChevronDownIcon
                v-if="layoutStore.isExpanded"
                class="w-4 h-4 transition-transform text-neutral-500 dark:text-slate-400 shrink-0"
                :class="{ 'rotate-180': item.open }"
              />
            </button>
            <div v-if="layoutStore.isExpanded && item.open" class="pl-4 space-y-1">
              <router-link
                v-for="child in item.children"
                :key="child.label"
                :to="child.path"
                class="flex items-center px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
                :class="{ 
                  'text-primary-700 dark:text-primary-400 bg-primary-50/50 dark:bg-slate-800/50': isActive(child.path),
                  'text-neutral-700 dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-200': !isActive(child.path)
                }"
              >
                {{ child.label }}
              </router-link>
            </div>
          </div>
        </template>
      </nav>

      <!-- User Footer -->
      <div class="shrink-0 p-4 border-t border-neutral-200 dark:border-slate-700/50 bg-linear-to-b from-slate-50 to-white dark:from-slate-800/90 dark:to-slate-800">
        <div class="flex items-center justify-between">
          <div 
            class="flex items-center min-w-0" 
            :class="{ 'justify-center w-full': !layoutStore.isExpanded }"
            @mouseenter="!layoutStore.isExpanded && showTooltip($event, userStore.fullName || t('common.user'))"
            @mouseleave="hideTooltip"
          >
            <div class="w-8 h-8 rounded-full bg-neutral-200 dark:bg-slate-700 flex items-center justify-center shrink-0">
              <UserIcon class="w-5 h-5 text-neutral-500 dark:text-slate-300" />
            </div>
            <div v-if="layoutStore.isExpanded" class="ml-3 min-w-0">
              <div class="text-sm font-medium text-neutral-900 dark:text-slate-200 truncate">{{ userStore.fullName }}</div>
              <div class="text-xs text-neutral-500 dark:text-slate-400 truncate">{{ userStore.user?.login }}</div>
            </div>
          </div>
          <button 
            v-if="layoutStore.isExpanded"
            class="p-2 hover:bg-neutral-100 dark:hover:bg-slate-800 rounded-lg shrink-0 transition-colors duration-200"
            :title="t('common.logout')"
            @click="handleLogout"
          >
            <ArrowRightOnRectangleIcon class="w-5 h-5 text-neutral-500 dark:text-slate-400 hover:text-neutral-700 dark:hover:text-slate-300" />
          </button>
        </div>
      </div>
    </aside>

    <!-- Collapsed Toggle Button -->
    <button
      v-if="!layoutStore.isExpanded"
      class="fixed top-4 left-[52px] p-2 bg-white dark:bg-slate-800 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:bg-neutral-50 dark:hover:bg-slate-700"
      style="z-index: 9999;"
      @click="layoutStore.toggleSidebar"
      @mouseenter="showTooltip($event, t('common.expand_sidebar'))"
      @mouseleave="hideTooltip"
    >
      <ChevronRightIcon class="w-4 h-4 text-neutral-500 dark:text-slate-300" />
    </button>

    <!-- Tooltip -->
    <BaseTooltip
      v-if="hoveredItem && !layoutStore.isExpanded"
      :text="hoveredItem"
      :x="tooltipPosition.x"
      :y="tooltipPosition.y"
    />
  </div>
</template>

<script setup lang="ts">
import {
ArrowRightOnRectangleIcon,
ArrowsRightLeftIcon,
ArrowUpTrayIcon,
ChartBarIcon,
ChevronDownIcon,
ChevronLeftIcon,
ChevronRightIcon,
FolderIcon,
MagnifyingGlassIcon,
QuestionMarkCircleIcon,
UserIcon
} from '@heroicons/vue/24/outline'
import type { Component } from 'vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { AuthService } from '../services/authService'
import { useLayoutStore } from '../stores/layout'
import { useUserStore } from '../stores/user'
import BaseTooltip from './BaseTooltip.vue'
import { isPreviewUser, PREVIEW_ROUTES, ROUTE_CFR_BIO_IMPORT_CREDIT_REPORT, ROUTE_CFR_CREDIT_RECONCILATION_SUMMARY_REPORT, ROUTE_IMPORT_MESSAGES, ROUTE_MASTERDATA_COUNTRY_CODES, ROUTE_MASTERDATA_LOCATIONS, ROUTE_MASTERDATA_PARTNERS, ROUTE_MASTERDATA_PRODUCT_GROUP_ENERGY_DENSITIES, ROUTE_MASTERDATA_PRODUCT_GROUPS, ROUTE_MASTERDATA_PRODUCTS, ROUTE_MOVEMENT_DATA_CATSCREDITS, ROUTE_MOVEMENT_DATA_CATSCREDITS_HISTORY, ROUTE_MOVEMENT_DATA_CONTRACTS, ROUTE_MOVEMENT_DATA_INBOUNDS, ROUTE_MOVEMENT_DATA_MASSBALANCES, ROUTE_MOVEMENT_DATA_OUTBOUNDS, ROUTE_MOVEMENT_DATA_SUSTAINABILITYDATA, ROUTE_REPORTS_ANNUAL_CREDIT_ADJUSTMENT, ROUTE_REPORTS_BIOFUEL_PURCHASES, ROUTE_REPORTS_CREDIT_GENERATION, ROUTE_REPORTS_OFFSITE_REPORT_FACILITY_INPUT, ROUTE_REPORTS_ONSITE_REPORT_FACILITY_INPUT, ROUTE_REPORTS_ONSITE_REPORT_FACILITY_OUTPUT, ROUTE_REPORTS_PRODUCT_MATERIAL_BALANCE, ROUTE_REPORTS_STANDALONE_CREDIT_SUMMARY, TENANT_CONFIGS } from '@/constants/tenants'
import type { TenantConfiguration } from '@/types/tenants'

interface NavigationItem {
  key?: string
  label: string
  path?: string
  icon: Component
  children?: Array<{ label: string; path: string }>
  open?: boolean
}

const authService = new AuthService();

const route = useRoute()
const router = useRouter()
const layoutStore = useLayoutStore()
const userStore = useUserStore()
const { t, locale } = useI18n()

// Tooltip logic
const hoveredItem = ref<string | null>(null)
const tooltipPosition = ref({ x: 0, y: 0 })

const showTooltip = (event: MouseEvent, label: string) => {
  if (layoutStore.isExpanded) return
  
  const target = event.currentTarget as HTMLElement
  const icon = target.querySelector('svg')
  const rect = icon ? icon.getBoundingClientRect() : target.getBoundingClientRect()
  
  tooltipPosition.value = {
    x: rect.right + 10,
    y: rect.top + rect.height / 2
  }
  hoveredItem.value = label
}

const hideTooltip = () => {
  hoveredItem.value = null
}

// Funktion zum Generieren der sprachabhängigen Pfade
const getLocalizedPath = (path: string) => {
  // Entferne führenden Sprachcode, falls vorhanden
  const pathWithoutLang = path.replace(/^\/[a-z]{2}\//, '/')
  // Füge aktuellen Sprachcode hinzu
  return `/${locale.value}${pathWithoutLang}`
}

const handleLogout = async () => {
  await authService.logout()
  await router.push(getLocalizedPath('/de/login'))
}

const tenantConfig = computed<TenantConfiguration | null>(() => TENANT_CONFIGS[authService.getTenant() || ''] || null);
const visibleRoutes = computed<string[]>(() => tenantConfig.value?.visibleRoutes || []);

// Main navigation (top)
const mainNavigationItems = ref<NavigationItem[]>([
  {
    label: t('navigation.bewegungsdaten'),
    icon: ArrowsRightLeftIcon,
    children: [
      { key: ROUTE_MOVEMENT_DATA_INBOUNDS, label: t('navigation.bewegungsdaten_children.einlieferungen'), path: getLocalizedPath('/de/movement-data/inbounds') },
      { key: ROUTE_MOVEMENT_DATA_OUTBOUNDS, label: t('navigation.bewegungsdaten_children.ablieferungen'), path: getLocalizedPath('/de/movement-data/outbounds') },
      { key: ROUTE_MOVEMENT_DATA_SUSTAINABILITYDATA, label: t('common.sustainabilityData'), path: getLocalizedPath('/de/movement-data/sustainabilitydata') },
      { key: ROUTE_MOVEMENT_DATA_MASSBALANCES, label: t('common.massBalances'), path: getLocalizedPath('/de/movement-data/mass-balances') },
      { key: ROUTE_MOVEMENT_DATA_CATSCREDITS, label: t('common.catsCredits'), path: getLocalizedPath('/de/movement-data/catscredits') },
      { key: ROUTE_MOVEMENT_DATA_CATSCREDITS_HISTORY, label: t('common.catsCreditsHistory'), path: getLocalizedPath('/de/movement-data/catscreditshistory') },
      { key: ROUTE_MOVEMENT_DATA_CONTRACTS, label: t('common.contracts'), path: getLocalizedPath('/de/movement-data/contracts') },
    ].filter(({ key }) => key !== undefined && visibleRoutes.value.includes(key)),
  },
  {
    label: t('navigation.stammdaten'),
    icon: FolderIcon,
    children: [
      { key: ROUTE_MASTERDATA_PARTNERS, label: t('navigation.stammdaten_children.partner'), path: getLocalizedPath('/de/masterdata/partners') },
      { key: ROUTE_MASTERDATA_LOCATIONS, label: t('navigation.stammdaten_children.standorte'), path: getLocalizedPath('/de/masterdata/locations') },
      // { label: t('common.feedstocks'), path: getLocalizedPath('/de/masterdata/feedstock') },
      { key: ROUTE_MASTERDATA_PRODUCTS, label: t('navigation.stammdaten_children.produkte'), path: getLocalizedPath('/de/masterdata/products') },
      { key: ROUTE_MASTERDATA_PRODUCT_GROUPS, label: t('common.productGroups'), path: getLocalizedPath('/de/masterdata/product-groups') },
      { key: ROUTE_MASTERDATA_PRODUCT_GROUP_ENERGY_DENSITIES, label: t('common.productGroupEnergyDensities'), path: getLocalizedPath('/de/masterdata/product-group-energy-densities') },
      { key: ROUTE_MASTERDATA_COUNTRY_CODES, label: t('navigation.stammdaten_children.laendercodes'), path: getLocalizedPath('/de/masterdata/country-codes') },
      { key: ROUTE_MASTERDATA_PRODUCTS, label: t('common.provinceCodes'), path: getLocalizedPath('/de/masterdata/province-codes') }
    ].filter(({ key }) => key !== undefined && visibleRoutes.value.includes(key)),
  },
  { key: ROUTE_IMPORT_MESSAGES, label: t('common.importMessages'), path: getLocalizedPath('/de/import-messages'), icon: ArrowUpTrayIcon },
  {
    label: t('navigation.reports'),
    icon: ChartBarIcon,
    children: [
      // { key: 'offsite-pu', label: t('common.offsitePu'), path: getLocalizedPath('/de/reports/offsite-pu') },
      // { key: 'onsite', label: t('common.onsiteProduction'), path: getLocalizedPath('/de/reports/onsite') },
      { key: ROUTE_REPORTS_ONSITE_REPORT_FACILITY_INPUT, label: t('common.onSiteReportFacilityInput'), path: getLocalizedPath('/de/reports/onsite-report-facility-input') },
      { key: ROUTE_REPORTS_ONSITE_REPORT_FACILITY_OUTPUT, label: t('common.onSiteReportFacilityOutput'), path: getLocalizedPath('/de/reports/onsite-report-facility-output') },
      { key: ROUTE_REPORTS_OFFSITE_REPORT_FACILITY_INPUT, label: t('common.offSiteReportFacilityInput'), path: getLocalizedPath('/de/reports/offsite-report-facility-input') },
      { key: ROUTE_REPORTS_PRODUCT_MATERIAL_BALANCE, label: t('common.productionMaterialBalanceReport'), path: getLocalizedPath('/de/reports/product-material-balance-report') },
      { key: ROUTE_REPORTS_CREDIT_GENERATION, label: t('common.creditGenerationReport'), path: getLocalizedPath('/de/reports/credit-generation-report') },
      { key: ROUTE_REPORTS_BIOFUEL_PURCHASES, label: t('common.biofuelPurchasesReport'), path: getLocalizedPath('/de/reports/biofuel-purchases-report') },
      { key: ROUTE_REPORTS_ANNUAL_CREDIT_ADJUSTMENT, label: t('common.annualCreditAdjustment'), path: getLocalizedPath('/de/reports/annual-credit-adjustment') },
      { key: ROUTE_REPORTS_STANDALONE_CREDIT_SUMMARY, label: t('common.standaloneAgreementCreditSummary'), path: getLocalizedPath('/de/reports/standalone-agreement-credit-summary') },
      { key: ROUTE_CFR_CREDIT_RECONCILATION_SUMMARY_REPORT, label: t('common.cfrCreditReconcilationSummaryReport'), path: getLocalizedPath(`/de/reports/${ROUTE_CFR_CREDIT_RECONCILATION_SUMMARY_REPORT}`) },
      { key: ROUTE_CFR_BIO_IMPORT_CREDIT_REPORT, label: t('common.cfrBioImportCreditReport'), path: getLocalizedPath(`/de/reports/${ROUTE_CFR_BIO_IMPORT_CREDIT_REPORT}`) },
    ].filter(({ key }) => key !== undefined && visibleRoutes.value.includes(key) && (!PREVIEW_ROUTES.includes(key) || isPreviewUser(userStore.user?.login || '')))
  }
].filter(({ children }) => (children === undefined || (children?.length || 0) > 0)))

// Footer navigation (bottom)
const footerNavigationItems = ref<NavigationItem[]>([
  {
    label: t('navigation.help'),
    icon: QuestionMarkCircleIcon,
    children: [
      { label: t('navigation.help_children.documentation'), path: getLocalizedPath('/de/hilfe/dokumentation') },
      { label: t('navigation.help_children.faq'), path: getLocalizedPath('/de/hilfe/faq') },
      { label: t('navigation.help_children.videos'), path: getLocalizedPath('/de/hilfe/hilfevideos') }
    ]
  },
  {
    label: t('navigation.user'),
    icon: UserIcon,
    children: [
      { label: t('navigation.user_children.dashboard'), path: getLocalizedPath('/de/user/dashboard') },
      { label: t('navigation.user_children.change_password'), path: getLocalizedPath('/de/user/passwort') },
      // { label: t('navigation.user_children.2fa_reset'), path: getLocalizedPath('/de/user/2fa-reset') },
      { label: t('navigation.user_children.settings'), path: getLocalizedPath('/de/user/einstellungen') },
      { label: t('navigation.user_children.messages'), path: getLocalizedPath('/de/user/nachrichten') },
      // Benutzeradministration nur für Admins anzeigen
      ...(userStore.isTenantAdmin ? [
        { 
          label: t('navigation.user_children.administration'), 
          path: getLocalizedPath('/de/user/administration') 
        },
        // Mandantenverwaltung für alle Admins sichtbar
        // {
        //   label: t('navigation.user_children.mandanten'),
        //   path: getLocalizedPath('/de/benutzer/mandanten')
        // }
      ] : []),
      { label: t('navigation.user_children.roles'), path: getLocalizedPath('/de/user/rollen') },
      { label: t('navigation.user_children.release_notes'), path: getLocalizedPath('/de/user/release-notes') }
    ]
  }
]);

const isActive = (path?: string) => {
  if (!path) return false
  return route.path === path
}

const isActiveParent = (item: NavigationItem) => {
  if (!item.children) return false
  return item.children.some(child => isActive(child.path))
}

const handleIconClick = async (item: NavigationItem) => {
  if (!layoutStore.isExpanded) {
    item.open = true
    if (item.children && item.children.length > 0) {
      await router.push(item.children[0].path)
    }
    await layoutStore.toggleSidebar()
  } else {
    item.open = !item.open
  }
}

// Watch für Sprachänderungen
watch(locale, () => {
  // Aktualisiere die Pfade in der Navigation
  mainNavigationItems.value.forEach(item => {
    if (item.children) {
      item.children.forEach(child => {
        child.path = getLocalizedPath(child.path)
      })
    }
  })
  
  footerNavigationItems.value.forEach(item => {
    if (item.children) {
      item.children.forEach(child => {
        child.path = getLocalizedPath(child.path)
      })
    }
  })
})
</script>

<style>
/* Your styles here */
</style>
